import React from 'react'
import { useList } from '@refinedev/core'
import { DateField } from '@refinedev/antd'
import { Button, Card, Skeleton, Space, Table, Typography } from 'antd'
import { brand } from '../../theme/brand'
import { Pill, type Tone } from '../../theme/listkit'
import { kindSpec } from './contentModeration'
import { useContentModeration } from './useContentModeration'

/**
 * Vouches card on the business listing show page (Task P4) — a full-width
 * table rendered BELOW the Listing | Checks+Decision row (not inside either
 * column), since it needs table width, not the half-width a form-shaped card
 * gets. Lets an admin remove or restore any vouch on this business without
 * waiting for a member to file a report first (vouches are public content;
 * browsing them needs no special justification the way a private owner note
 * would — see docs/JAGUAR-OWNED.md's Moderation section).
 *
 * Reuses the P3 `useContentModeration('business_vouch')` hook verbatim —
 * same confirm dialog, same messages, same double-submit guard — so an
 * admin write that actually changes a row lands in the admin audit log
 * exactly like one from `ReportedContent` (the report-page equivalent), just
 * without a report to stamp (there isn't one).
 *
 * Refresh mechanism (fix round 2, item 1): this component makes NO refresh
 * call of its own after a remove/restore — no `refetch()`, no
 * `useInvalidate()`. `useContentModeration`'s own `useUpdate` (Refine core)
 * already invalidates `business_vouches` list queries in its internal
 * `onSettled` — which fires for EVERY mutation outcome (success, zero-row
 * "success", or a thrown error) and runs BEFORE the per-call `onSuccess`/
 * `onError` that resolve `remove()`/`restore()`'s own promise — using
 * Refine's default `invalidates: ['list','many','detail']`. That already
 * covers this component's own `useList('business_vouches', …)`, so a second
 * invalidate/refetch here would only race it: `query.refetch()`'s default
 * `cancelRefetch: true` would cancel that already-in-flight auto-refetch and
 * restart it, and even a `useInvalidate()` call adds nothing the hook's own
 * `onSettled` hasn't already triggered. A cancelled confirm never calls
 * `mutate()` at all, so nothing is invalidated and nothing needed refreshing
 * — the list was never stale. The one exception is the error state's own
 * "Try again" button below, which is a manual, user-initiated `refetch()`
 * with no automatic invalidation racing it.
 *
 * Acting lock (fix round 2, item 2): `actingId` is a COMPONENT-WIDE lock, not
 * a per-row one — it is set the instant ANY row's Remove/Restore is clicked
 * (before the confirm dialog even opens), and every row's button reads
 * `disabled={isPending || actingId !== null}`, so no other row can be
 * clicked while one is mid-flow, closing the gap a per-row lock left open
 * during the confirm-dialog-open window (the P3 hook's own `busyRef` already
 * blocks a second `remove()`/`restore()` call there, but before this fix a
 * DIFFERENT row's button still looked clickable). Only the acting row shows
 * `loading`. `actingId` clears only once the action has resolved (tracked by
 * `actionDone`) AND `query.isFetching` is false — an effect keyed on both,
 * so it can't fire on a stale read of either: a cancelled confirm never
 * triggers a fetch, so `isFetching` is already false and the lock clears at
 * once; a real write's `useUpdate`-triggered refetch is already under way by
 * the time `remove()`/`restore()` resolves, so the effect waits for that
 * fetch (successful or not — a refetch that itself errors still flips
 * `isFetching` back to false) to actually settle before releasing the lock.
 */

const RELATIONSHIP_LABEL: Record<string, string> = {
  hired: 'Hired them',
  bought: 'Bought from them',
  worked_with: 'Worked with them',
  classmate: 'Classmate or friend',
}

const DISCLOSURE_LABEL: Record<string, string> = {
  family: 'Family',
  employee: 'Employee',
}

interface VouchAuthor {
  name: string | null
  class_year: string | null
}

interface VouchRow {
  id: string
  business_id: string
  relationship: string
  sentence: string
  disclosure: string
  status: string
  created_at: string
  author?: VouchAuthor | null
}

function statusTone(status: string): Tone {
  return status === 'removed' ? 'red' : 'green'
}

function statusLabel(status: string): string {
  return status === 'removed' ? 'Removed' : 'Visible'
}

const headStyle: React.CSSProperties = { fontFamily: brand.fDisplay, fontWeight: 700, color: brand.ink }

const mutedText: React.CSSProperties = { fontFamily: brand.fSans, fontSize: 13, color: brand.sub }

export function VouchesCard({ businessId }: { businessId: string }) {
  const spec = kindSpec('business_vouch')!
  const { query, result } = useList<VouchRow>({
    resource: 'business_vouches',
    filters: [{ field: 'business_id', operator: 'eq', value: businessId }],
    sorters: [{ field: 'created_at', order: 'desc' }],
    pagination: { mode: 'off' },
    meta: { select: spec.select },
    // No automatic retry: a simple list with its own manual "Try again"
    // button should surface a failure right away, not spend several seconds
    // silently retrying behind the loading skeleton.
    queryOptions: { retry: false },
  })
  const { remove, restore, isPending } = useContentModeration('business_vouch')
  // Component-wide acting lock (fix round 2, item 2) — see the module doc
  // comment above for the full clear-timing rationale.
  const [actingId, setActingId] = React.useState<string | null>(null)
  const [actionDone, setActionDone] = React.useState(false)

  React.useEffect(() => {
    if (actionDone && !query.isFetching) {
      setActingId(null)
      setActionDone(false)
    }
  }, [actionDone, query.isFetching])

  const rows = result?.data ?? []
  const visibleCount = rows.filter((r) => r.status !== 'removed').length
  const hasData = !query.isLoading && !query.isError
  const locked = isPending || actingId !== null

  async function handleRemove(id: string) {
    setActingId(id)
    await remove(id)
    setActionDone(true)
  }

  async function handleRestore(id: string) {
    setActingId(id)
    await restore(id)
    setActionDone(true)
  }

  const title = hasData ? `Vouches · ${visibleCount} visible` : 'Vouches'

  return (
    <Card title={<span style={headStyle}>{title}</span>} style={{ marginBottom: 16 }}>
      {query.isLoading ? (
        <Skeleton active />
      ) : query.isError ? (
        <Space direction="vertical" size={8}>
          <Typography.Text style={mutedText}>Couldn&apos;t load vouches.</Typography.Text>
          <Button size="small" onClick={() => query.refetch()}>
            Try again
          </Button>
        </Space>
      ) : rows.length === 0 ? (
        <Typography.Text style={{ ...mutedText, fontStyle: 'italic' }}>No vouches yet.</Typography.Text>
      ) : (
        <Table<VouchRow> dataSource={rows} rowKey="id" pagination={false} scroll={{ x: true }}>
          <Table.Column<VouchRow>
            title="Member"
            render={(_: unknown, row) => (
              <span style={{ fontFamily: brand.fSans, fontSize: 13, color: brand.ink }}>
                {row.author?.name ?? 'Unknown member'}
                {row.author?.class_year ? ` · Class of ${row.author.class_year}` : ''}
              </span>
            )}
          />
          <Table.Column<VouchRow>
            title="Relationship"
            dataIndex="relationship"
            render={(v: string) => RELATIONSHIP_LABEL[v] ?? v}
          />
          <Table.Column<VouchRow> title="Vouch" dataIndex="sentence" />
          <Table.Column<VouchRow>
            title="Disclosure"
            dataIndex="disclosure"
            render={(v: string) => DISCLOSURE_LABEL[v] ?? '—'}
          />
          <Table.Column<VouchRow>
            title="Status"
            render={(_: unknown, row) => <Pill tone={statusTone(row.status)}>{statusLabel(row.status)}</Pill>}
          />
          <Table.Column<VouchRow>
            title="Added"
            dataIndex="created_at"
            render={(v: string) => <DateField value={v} format="MMM D, YYYY" />}
          />
          <Table.Column<VouchRow>
            title="Actions"
            render={(_: unknown, row) =>
              row.status === 'removed' ? (
                <Button
                  size="small"
                  loading={actingId === row.id}
                  disabled={locked}
                  data-testid={`vouch-restore-${row.id}`}
                  onClick={() => void handleRestore(row.id)}
                >
                  Restore
                </Button>
              ) : (
                <Button
                  size="small"
                  danger
                  loading={actingId === row.id}
                  disabled={locked}
                  data-testid={`vouch-remove-${row.id}`}
                  onClick={() => void handleRemove(row.id)}
                >
                  Remove
                </Button>
              )
            }
          />
        </Table>
      )}
    </Card>
  )
}
